import axios from 'axios';

import Button from 'components/button';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import Header from 'components/text/header';
import UseApi from 'hooks/useApi';
import useForm from 'hooks/useForm';
import { useParams, useNavigate } from 'react-router-dom';
import { ChangeEvent, useState, useEffect } from 'react';
import ImageUpload from 'components/imageUpload';

// Define a type compatible with the useForm hook (string values only)
interface FormValues {
  email: string;
  password: string;
  username: string;
  mobile: string;
  address: string;
  birthDate: string;
  picture: string;
  referrer: string;
  isAffiliate: string;
  [key: string]: string; // Index signature for string indexing
}

const initialValues: FormValues = {
  email: '',
  password: '',
  username: '',
  mobile: '',
  address: '',
  birthDate: '',
  picture: '',
  referrer: '',
  isAffiliate: 'false',
};

interface IProps {
  formValues?: any;
  type: 'Create' | 'Update';
  onSubmit?: () => void;
  handleToggleCreatePackage?: () => void;
}

const UserForm: React.FC<IProps> = ({ formValues, type, onSubmit, handleToggleCreatePackage }) => {
  const { patchApi, postApi, getApi } = UseApi();
  const navigate = useNavigate();
  const [pictureUrl, setPictureUrl] = useState<string>(
    formValues?.picture || '',
  );
  const [bookings, setBookings] = useState<any[]>([]);
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);

  const getRequiredFields = () => {
    if (type === 'Create') {
      return ['email', 'username', 'password'];
    }
    return ['email', 'username'];
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bookingsRes = await getApi('bookings');

        if (bookingsRes?.data) {
          setBookings(bookingsRes.data.data.bookings || []);
        }

        // Initialize selected bookings if formValues exist
        if (formValues?.tripHistory && Array.isArray(formValues.tripHistory)) {
          setSelectedBookings(
            formValues.tripHistory.map((booking: any) =>
              typeof booking === 'object' ? booking._id : booking,
            ),
          );
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const prepareSubmitData = () => {
    const submitData = {
      email: values.email,
      username: values.username,
      mobile: values.mobile,
      address: values.address,
      birthDate: values.birthDate,
      picture: pictureUrl,
      referrer: values.referrer,
      isAffiliate: values.isAffiliate === 'true',
      tripHistory: selectedBookings,
      password: values.password || undefined, 
    };

   

    return submitData;
  };

  const createUser = async () => {
    const submitData = prepareSubmitData();

    let res;
    if (type === 'Update') {
      res = await patchApi(`users/${id}`, submitData);
    } else {
      res = await postApi(`users`, submitData);
    }
    if (res?.data.message) {
      resetForm();
      onSubmit?.();
      handleToggleCreatePackage?.();
    }
  };

  function prepareInitialValues(doc: any): FormValues {
    // Keep only string values for useForm compatibility
    const flatValues: FormValues = {
      email: doc.email || '',
      password: '', // Don't populate password field
      username: doc.username || '',
      mobile: doc.mobile || '',
      address: doc.address || '',
      birthDate: doc.birthDate || '',
      picture: doc.picture || '',
      referrer: doc.referrer || '',
      isAffiliate: doc.isAffiliate?.toString() || 'false',
    };

    return flatValues;
  }

  const { values, handleChange, resetForm, handleSubmit } = useForm(
    formValues ? prepareInitialValues(formValues) : initialValues,
    createUser,
    getRequiredFields() // Pass only the required fields
  );
  const { id } = useParams();

  // Custom handler for checkbox
  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const syntheticEvent = {
      target: {
        name,
        value: checked ? 'true' : 'false',
      },
    } as ChangeEvent<HTMLInputElement>;

    handleChange(syntheticEvent);
  };

  // Custom handler for image uploads
  const handleImageChange = (e: any) => {
    if (e.target && e.target.value) {
      // Extract the URL string
      if (typeof e.target.value === 'object' && e.target.value.data) {
        setPictureUrl(e.target.value.data);
      } else {
        setPictureUrl(e.target.value);
      }
    }
  };

  // Handle multi-select for trip history
  const handleBookingSelection = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setSelectedBookings(selectedOptions);
  };

  return (
    <Card extra="p-4 mb-4">
      <Header>{type} User</Header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Basic Info Section */}
        <InputField
          variant="auth"
          extra="mb-3"
          label="Email*"
          placeholder="Enter email address"
          id="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          name="email"
        />

        {type === 'Create' && (
          <InputField
            variant="auth"
            extra="mb-3"
            label="Password*"
            placeholder="Enter password"
            id="password"
            type="password"
            value={values.password}
            onChange={handleChange}
            name="password"
          />
        )}

        <InputField
          variant="auth"
          extra="mb-3"
          label="Username*"
          placeholder="Enter username"
          id="username"
          type="text"
          value={values.username}
          onChange={handleChange}
          name="username"
        />

        <InputField
          variant="auth"
          extra="mb-3"
          label="Mobile Number"
          placeholder="Enter mobile number"
          id="mobile"
          type="text"
          value={values.mobile}
          onChange={handleChange}
          name="mobile"
        />

        <InputField
          variant="auth"
          extra="mb-3"
          label="Address"
          placeholder="Enter address"
          id="address"
          type="text"
          value={values.address}
          onChange={handleChange}
          name="address"
        />

        <InputField
          variant="auth"
          extra="mb-3"
          label="Birth Date"
          placeholder="Enter birth date"
          id="birthDate"
          type="date"
          value={values.birthDate?.substring(0, 10)}
          onChange={handleChange}
          name="birthDate"
        />

        <div className="col-span-1 mb-3 md:col-span-2">
          <ImageUpload
            id="picture"
            name="picture"
            label="Profile Picture"
            value={pictureUrl}
            onChange={handleImageChange}
            error={null}
            folder="users"
          />
        </div>

        <InputField
          variant="auth"
          extra="mb-3"
          label="Referrer"
          placeholder="Enter referrer"
          id="referrer"
          type="text"
          value={values.referrer}
          onChange={handleChange}
          name="referrer"
        />

        <div className="mb-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={values.isAffiliate === 'true'}
              onChange={handleCheckboxChange}
              name="isAffiliate"
              className="mr-2"
            />
            <span>Is Affiliate</span>
          </label>
        </div>

        {/* Related Data Section */}
        <div className="col-span-1 mb-3 md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Trip History
          </label>
          <select
            multiple
            className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            value={selectedBookings}
            onChange={handleBookingSelection}
            size={5}
          >
            {bookings.map((booking: any) => (
              <option key={booking._id} value={booking._id}>
                Booking #{booking.bookingId} -{' '}
                {booking.package?.packageName || 'Unknown Package'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Hold Ctrl/Cmd to select multiple bookings
          </p>
        </div>
      </div>

      <Button extraClasses="mx-auto mt-4" onClick={handleSubmit}>
        {type}
      </Button>
    </Card>
  );
};

export default UserForm;